import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import {
  normalizeCasinoFinanceKpisResponse,
  normalizeCasinoFinanceResponse,
} from "./casinoFinanceReportNormalizer";

/**
 * Token from `localStorage` and a private admin panel client (shared by summary + KPI requests).
 */
function getAuthenticatedInstance() {
  let token = null;
  try {
    token = JSON.parse(localStorage.getItem("token"));
  } catch {
    token = null;
  }
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

/**
 * @param {Record<string, string | number | undefined>} params Expects start_date, end_date (YYYY-MM-DD).
 * Optional: limit, cursor (cursor pagination — PAMENG-1720 / PAMENG-1779), page (offset-style BE).
 */
function omitEmptyParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  );
}

/**
 * Parses one page from GET /admin-panel-v2/casino/analytics/summary (PAMENG-1720).
 * @param {unknown} raw Axios response body (may be `{ data: { items, next_cursor } }`).
 */
export function parseAnalyticsSummaryPage(raw) {
  if (!raw || typeof raw !== "object") {
    return { items: [], next_cursor: null };
  }
  const inner =
    raw.data && typeof raw.data === "object" ? raw.data : raw;
  if (!inner || typeof inner !== "object") {
    return { items: [], next_cursor: null };
  }
  const items = Array.isArray(inner.items) ? inner.items : [];
  const next_cursor =
    inner.next_cursor ?? inner.nextCursor ?? inner.cursor_next ?? null;
  return {
    items,
    next_cursor: next_cursor || null,
  };
}

const DEFAULT_PAGE_LIMIT = 20;

/**
 * Shared query fields for /casino/analytics/summary/kpis and /casino/analytics/summary.
 * `money_type` is sent as a comma-separated list when multiple values are selected.
 * @param {Record<string, string | string[] | undefined>} filters
 * @returns {Record<string, string | number | undefined>}
 */
const ALL = "all";

function analyticsFilterQueryParams(filters = {}) {
  const { money_type, game, subprovider } = filters;
  const out = {};
  if (Array.isArray(money_type) && money_type.length > 0) {
    out.money_type = money_type.join(",");
  } else if (typeof money_type === "string" && money_type.length > 0) {
    out.money_type = money_type;
  }
  if (game != null && String(game) !== "" && String(game) !== ALL) {
    out.game = String(game);
  }
  // UI "Provider" maps to subprovider in analytics contracts (not top-level provider).
  if (subprovider != null && String(subprovider) !== "" && String(subprovider) !== ALL) {
    out.subprovider = String(subprovider);
  }
  return out;
}

/**
 * Fetches one page of casino finance summary (cursor pagination; optional `page` for BE variants).
 */
export async function fetchCasinoFinanceReport(params = {}) {
  const start_date = params.start_date;
  const end_date = params.end_date;
  if (!start_date || !end_date) {
    return normalizeCasinoFinanceResponse({ data: { items: [] } });
  }

  const instance = getAuthenticatedInstance();

  const limit = Number(params.limit) > 0 ? Number(params.limit) : DEFAULT_PAGE_LIMIT;
  const cursor = params.cursor ?? null;
  const page = params.page;

  const { data } = await instance.get(API_ROUTES.GET_CASINO_ANALYTICS_SUMMARY, {
    params: omitEmptyParams({
      start_date,
      end_date,
      limit,
      cursor,
      page,
      ...analyticsFilterQueryParams({
        money_type: params.money_type,
        game: params.game,
        subprovider: params.subprovider,
      }),
    }),
  });

  return normalizeCasinoFinanceResponse(data);
}

/**
 * Fetches aggregate casino finance KPIs for the selected date window (game/provider filters not supported).
 */
export async function fetchCasinoFinanceKpis(params = {}) {
  const start_date = params.start_date;
  const end_date = params.end_date;
  if (!start_date || !end_date) {
    return { summary: null, filters: [] };
  }

  const instance = getAuthenticatedInstance();

  const { data } = await instance.get(API_ROUTES.GET_CASINO_ANALYTICS_KPIS, {
    params: omitEmptyParams({
      start_date,
      end_date,
      ...analyticsFilterQueryParams({
        money_type: params.money_type,
      }),
    }),
  });

  return normalizeCasinoFinanceKpisResponse(data);
}
